import { useAuth0 } from "@auth0/auth0-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  IdeaCustomization,
  IdeaCustomizationCreateParams,
  IdeaCustomizationUpdateParams,
  IdeaPerspectivesUpdateParams,
  IdeaFiltersUpdateParams,
  IdeaColumnsUpdateParams,
  IdeaInfoTabsUpdateParams,
  Customization,
  UpdateFiltersFromModalParams
} from "@bageltechnologies/bagel-common";
import {
  getCustomizations,
  getCustomization,
  createCustomization,
  updateCustomization,
  deleteCustomization,
  getAvailablePerspectives,
  getPerspectives,
  updatePerspectives,
  deletePerspective,
  getFilters,
  getFilterConfig,
  updateFilters,
  updateFiltersFromModal,
  getIdeaColumns,
  updateIdeaColumns,
  getInfoTabs,
  updateInfoTabs,
  AvailablePerspective,
  
} from "../api/customizations";

// Query keys
export const customizationsKeys = {
  all: ["customizations"] as const,
  list: () => [...customizationsKeys.all, "list"] as const,
  detail: () => [...customizationsKeys.all, "detail"] as const,
  sections: {
    availablePerspectives: () => [...customizationsKeys.all, "available-perspectives"] as const,
    perspectives: () => [...customizationsKeys.all, "perspectives"] as const,
    filters: () => [...customizationsKeys.all, "filters"] as const,
    filterConfig: () => [...customizationsKeys.all, "filter-config"] as const,
    ideaColumns: () => [...customizationsKeys.all, "idea-columns"] as const,
    infoTabs: () => [...customizationsKeys.all, "info-tabs"] as const,
  },
};

// Main Customization Hooks
export const useCustomizations = () => {
  const auth0 = useAuth0();

  return useQuery({
    queryKey: customizationsKeys.list(),
    queryFn: () => getCustomizations(auth0),
    enabled: !!auth0,
  });
};

export const useCustomization = () => {
  const auth0 = useAuth0();

  return useQuery({
    queryKey: customizationsKeys.detail(),
    queryFn: () => getCustomization(auth0),
    enabled: !!auth0,
  });
};

export const useCustomizationActions = () => {
  const auth0 = useAuth0();
  const queryClient = useQueryClient();

  const createMutation = useMutation({
    mutationFn: (customization: IdeaCustomizationCreateParams) =>
      createCustomization(customization, auth0),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: customizationsKeys.list() });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (customization: IdeaCustomizationUpdateParams) =>
      updateCustomization(customization, auth0),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: customizationsKeys.list() });
      queryClient.invalidateQueries({ queryKey: customizationsKeys.detail() });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteCustomization(auth0),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: customizationsKeys.list() });
    },
  });

  return {
    createCustomization: createMutation.mutateAsync,
    updateCustomization: updateMutation.mutateAsync,
    deleteCustomization: deleteMutation.mutateAsync,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
};

// Section-Specific Hooks

// Perspectives
export const useAvailablePerspectives = () => {
  const auth0 = useAuth0();

  return useQuery({
    queryKey: customizationsKeys.sections.availablePerspectives(),
    queryFn: () => getAvailablePerspectives(auth0),
    enabled: !!auth0,
  });
};

export const usePerspectives = () => {
  const auth0 = useAuth0();

  return useQuery({
    queryKey: customizationsKeys.sections.perspectives(),
    queryFn: () => getPerspectives(auth0),
    enabled: !!auth0,
  });
};

export const useUpdatePerspectives = () => {
  const auth0 = useAuth0();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: IdeaPerspectivesUpdateParams) =>
      updatePerspectives(data, auth0),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: customizationsKeys.sections.perspectives() });
      queryClient.invalidateQueries({ queryKey: customizationsKeys.detail() });
    },
  });
};

export const useDeletePerspective = () => {
  const auth0 = useAuth0();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ perspectiveKey }: { perspectiveKey: string }) =>
      deletePerspective(perspectiveKey, auth0),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: customizationsKeys.sections.perspectives() });
      queryClient.invalidateQueries({ queryKey: customizationsKeys.detail() });
    },
  });
};

// Filters
export const useFilters = () => {
  const auth0 = useAuth0();

  return useQuery({
    queryKey: customizationsKeys.sections.filters(),
    queryFn: () => getFilters(auth0),
    enabled: !!auth0,
  });
};

export const useFilterConfig = () => {
  const auth0 = useAuth0();

  return useQuery({
    queryKey: customizationsKeys.sections.filterConfig(),
    queryFn: () => getFilterConfig(auth0),
    enabled: !!auth0,
  });
};

export const useUpdateFilters = () => {
  const auth0 = useAuth0();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: IdeaFiltersUpdateParams) =>
      updateFilters(data, auth0),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: customizationsKeys.sections.filters() });
      queryClient.invalidateQueries({ queryKey: customizationsKeys.detail() });
    },
  });
};

// Idea Columns
export const useIdeaColumns = () => {
  const auth0 = useAuth0();

  return useQuery({
    queryKey: customizationsKeys.sections.ideaColumns(),
    queryFn: () => getIdeaColumns(auth0),
    enabled: !!auth0,
  });
};

export const useUpdateIdeaColumns = () => {
  const auth0 = useAuth0();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: IdeaColumnsUpdateParams) =>
      updateIdeaColumns(data, auth0),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: customizationsKeys.sections.ideaColumns() });
      queryClient.invalidateQueries({ queryKey: customizationsKeys.detail() });
    },
  });
};

// Info Tabs
export const useInfoTabs = () => {
  const auth0 = useAuth0();

  return useQuery({
    queryKey: customizationsKeys.sections.infoTabs(),
    queryFn: () => getInfoTabs(auth0),
    enabled: !!auth0,
  });
};

export const useUpdateInfoTabs = () => {
  const auth0 = useAuth0();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: IdeaInfoTabsUpdateParams) =>
      updateInfoTabs(data, auth0),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: customizationsKeys.sections.infoTabs() });
      queryClient.invalidateQueries({ queryKey: customizationsKeys.detail() });
    },
  });
}; 

export const useUpdateIdeaFilters = () => {
  const auth0 = useAuth0();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: IdeaFiltersUpdateParams) =>
      updateFilters(data, auth0),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: customizationsKeys.sections.filters() });
      queryClient.invalidateQueries({ queryKey: customizationsKeys.detail() });
    },
  });
};

export const useUpdateEvidenceFilters = () => {
  const auth0 = useAuth0();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: IdeaFiltersUpdateParams) =>
      updateFilters(data, auth0),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: customizationsKeys.sections.filters() });
      queryClient.invalidateQueries({ queryKey: customizationsKeys.detail() });
    },
  });
};


export const useUpdateFiltersFromModal = () => {
  const auth0 = useAuth0();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateFiltersFromModalParams) =>
      updateFiltersFromModal(data, auth0),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: customizationsKeys.sections.filters() });
      queryClient.invalidateQueries({ queryKey: customizationsKeys.detail() });
    },
  });
};